import { useState } from "react";
import { bool, number, oneOfType, string } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useEstimateStructure } from "@tools";
import { TooltipIcon, Dialog, ButtonSwitch, InfoLabel } from "@components";
import { Grid, IconButton } from "@material-ui/core";
import { TimelineSettingsContainer } from "./components/TimelineSettingsContainer";
import { SectionContainer } from "./components/SectionContainer/SectionContainer";
import { NonSectionWTContainer } from "./components/SectionContainer/NonSectionWTContainer";
import { Settings } from "@assets";
import { Close } from "@material-ui/icons";
import classnames from "classnames";

export const TimelineSettings = observer(({
  small,
  className,
  useWeekMonthSwitch,
  ...containerProps
}) => {
  const { t } = useTranslation();
  const {
    settings,
    sections,
    workTypesHaveFixedSummaryHours
  } = useEstimateStructure();
  const { useTimelineSections, isTimelineTableUsed, useWeekTime } = settings;

  const [visible, show] = useState(false);

  const handlePopup = () => show(!visible);
  const handleClose = () => show(false);
  
  const handleProjectTimeModifierChange = (value) => {
    settings.setUseWeekTime(value);
  }

  return (
    <>
      <TooltipIcon
        icon={<Settings />}
        onClick={handlePopup}
        style={{ right: small && -8 }}
        color="primary"
        className={classnames(className, small && "absolute text-sm")}
      >
        {t("views.editor.dialogs.timeline_settings.wta_change")}
      </TooltipIcon>

      <Dialog
        open={visible}
        onClose={handleClose}
        width={containerProps.fullSettings ? 920 : 620}
      >
        <Grid
          item
          container
          justifyContent="space-between"
          alignItems="center"
          className="pb-3"
        >
          <Grid item container alignItems="center" className="w-max">
            <h3>{t("views.editor.dialogs.timeline_settings.wta_change")}</h3>
            <TooltipIcon color="primary">
              {t("views.editor.dialogs.timeline_settings.wta_tooltip")}
            </TooltipIcon>
          </Grid>
          <IconButton
            color="primary"
            size="small"
            aria-label="close modal"
            onClick={handlePopup}
          >
            <Close />
          </IconButton>
        </Grid>
        {
          useWeekMonthSwitch &&
          <Grid item container className="my-2">
            <InfoLabel className="mt-0" label="views.editor.time_in" />
            <ButtonSwitch
              value={useWeekTime || false}
              setValue={handleProjectTimeModifierChange}
              values={[
                { value: true, label: t("times.week") },
                { value: false, label: t("times.month") },
              ]}
              fullWidth
              className="mb-4"
              size="small"
            />
          </Grid>
        }
        {
          useTimelineSections && isTimelineTableUsed
            ? sections.map((section, i) => (
              <SectionContainer
                key={section.id}
                section={section}
                isFirst={i === 0}
                {...containerProps}
              />
            ))
            : <TimelineSettingsContainer {...containerProps} />
        }
        {
          useTimelineSections && isTimelineTableUsed && workTypesHaveFixedSummaryHours &&
          <NonSectionWTContainer {...containerProps} />
        }
      </Dialog>
    </>
  );
});

TimelineSettings.propTypes = {
  small: bool,
  fullSettings: bool,
  className: string,
  selectedWorkType: number,
  useWeekMonthSwitch: bool,
  selectedResource: oneOfType([string, number]),
}